import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import api, { BASE_URL, fotoUrl } from '../api';
import { useOkunmamisSayisi } from '../hooks/useOkunmamisSayisi';
import { useYeniAdaySayisi } from '../hooks/useYeniAdaySayisi';
import { useBekleyenBirlestirmeSayisi } from '../hooks/useBekleyenBirlestirmeSayisi';
import { useYeniReferansSayisi } from '../hooks/useYeniReferansSayisi';
import { useTheme } from '../contexts/ThemeContext';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { kullanici, cikisYap } = useAuth();
  const location = useLocation();
  const okunmamis      = useOkunmamisSayisi();
  const yeniAday       = useYeniAdaySayisi();
  const bekleyenBirlestirme = useBekleyenBirlestirmeSayisi(kullanici?.rol !== 'super_admin');
  const yeniReferans = useYeniReferansSayisi(kullanici?.rol !== 'super_admin');
  const [arsivErisimVar, setArsivErisimVar] = useState(kullanici?.rol === 'super_admin');
  const { isDark, toggle } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Sayfa değişince sidebar kapat
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Süper admin dışındaki kullanıcılar için arşiv erişimi var mı kontrolü (menüde göstermek için)
  useEffect(() => {
    if (kullanici?.rol === 'super_admin') { setArsivErisimVar(true); return; }
    api.get('/api/arsiv/erisim/ben')
      .then(r => setArsivErisimVar(!!r.data.erisim)).catch(() => setArsivErisimVar(false));
  }, [kullanici?.rol]);

  const isSuperAdminHome = kullanici?.rol === 'super_admin';

  const menuItems = isSuperAdminHome
    ? [
        { path: '/',                   label: 'Ana Panel',          icon: '▦' },
        { path: '/subeler',            label: 'Şubeler',            icon: '🏢' },
        { path: '/crm',                label: 'CRM',                icon: '◈' },
        { path: '/loglar',             label: 'Loglar',             icon: '📋' },
        { path: '/otomatik-postalar',  label: 'Otomatik Postalar',  icon: '✉' },
        { path: '/whatsapp-ai',        label: 'WhatsApp AI',         icon: '🤖' },
        { path: '/ogrenciler',         label: 'Öğrenciler',          icon: '◎' },
        { path: '/tum-kullanicilar',   label: 'Tüm Kullanıcılar',    icon: '◎' },
        { path: '/personel',           label: 'Personel',            icon: '🛎️' },
        { path: '/tp-ayarlari',        label: 'TP Ayarları',         icon: '⭐' },
        { path: '/tp-listesi',         label: 'TP Listesi',          icon: '📋' },
        { path: '/arsiv',              label: 'Arşiv',               icon: '🗄️' },
      ]
    : [
        { path: '/',                          label: 'Genel Bakış',       icon: '▦' },
        { path: '/crm',                        label: 'CRM',               icon: '◈' },
        { path: '/ogrenciler',                 label: 'Öğrenciler',        icon: '◎' },
        { path: '/egitmenler',                 label: 'Eğitmenler',        icon: '◐' },
        { path: '/salonlar',                   label: 'Salonlar',          icon: '⬡' },
        { path: '/dersler',                    label: 'Dersler',           icon: '◫' },
        { path: '/program',                    label: 'Program',           icon: '⊟' },
        { path: '/muhasebe',                   label: 'Muhasebe',          icon: '₺' },
        { path: '/kasa',                       label: 'Kasa',              icon: '💵' },
        { path: '/etkinlikler',                label: 'Etkinlikler',       icon: '🎭' },
        { path: '/kredi-paketleri',            label: 'Kredi Paketleri',   icon: '💳' },
        { path: '/takvim',                     label: 'Stüdyo Takvimi',    icon: '📆' },
        { path: '/katilim',                    label: 'Katılım Teyit',     icon: '✓' },
        { path: '/risk-raporu',                label: 'Risk Raporu',       icon: '◉' },
        { path: '/birlestirme-talepleri',      label: 'Hesap Birleştirme', icon: '🔗' },
        { path: '/referans-yonetim',           label: 'Referans',          icon: '🎁' },
        { path: '/mesajlar',                   label: 'Mesajlar',          icon: '✉' },
        ...(arsivErisimVar ? [{ path: '/arsiv', label: 'Arşiv', icon: '🗄️' }] : []),
      ];

  const subeGirisi = !!localStorage.getItem('sube_giren_token_backup') || !!localStorage.getItem('impersonating_as');
  const topOffset = subeGirisi ? 36 : 0;

  const S = {
    sidebar: {
      width: 220, height: '100vh',
      background: 'var(--sidebar-bg)',
      borderRight: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column' as const,
      transition: 'background .2s',
    },
    sidebarHeader: {
      padding: '20px 18px 16px',
      borderBottom: '1px solid var(--border)',
    },
    logoText: { fontWeight: 700, fontSize: 14, letterSpacing: -0.3, color: 'var(--text)' },
    logoSub: { fontSize: 10, color: 'var(--text-muted)', marginTop: 2 },
    nav: { padding: '8px 6px', flex: 1, overflowY: 'auto' as const },
    navItem: (active: boolean) => ({
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 10px', borderRadius: 7, marginBottom: 1,
      textDecoration: 'none', fontSize: 13,
      background: active ? 'var(--active-bg)' : 'transparent',
      color: active ? 'var(--active-color)' : 'var(--text-secondary)',
      fontWeight: active ? 600 : 400,
      transition: 'background .12s, color .12s',
      borderLeft: active ? '2px solid var(--active-border)' : '2px solid transparent',
    }),
    sidebarFooter: {
      padding: '10px 6px',
      borderTop: '1px solid var(--border)',
    },
    userRow: {
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '7px 10px',
    },
    userName: { fontSize: 12, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
    userRole: { fontSize: 10, color: 'var(--text-muted)' },
    iconBtn: {
      border: 'none', background: 'none', cursor: 'pointer',
      fontSize: 16, color: 'var(--text-muted)', padding: 4, borderRadius: 6,
      lineHeight: 1, transition: 'color .12s',
    },
    main: {
      flex: 1, overflow: 'auto',
      background: 'var(--bg)',
      transition: 'background .2s',
    },
  };

  return (
    <div style={{ display: 'flex', height: '100vh', paddingTop: topOffset }}>

      {/* Mobil overlay */}
      <div
        className={`sidebar-overlay${sidebarOpen ? ' open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <div className={`layout-sidebar${sidebarOpen ? ' open' : ''}`} style={S.sidebar}>
        <div style={S.sidebarHeader}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={S.logoText}>istanbulTANGO</div>
              <div style={S.logoSub}>
                {isSuperAdminHome ? 'Ana Panel' : (kullanici?.sube_adi ?? 'Yönetim Paneli')}
              </div>
            </div>
            {/* Dark mode toggle */}
            <button onClick={toggle} style={S.iconBtn} title={isDark ? 'Açık mod' : 'Koyu mod'}>
              {isDark ? '☀️' : '🌙'}
            </button>
          </div>
        </div>

        <nav style={S.nav}>
          {menuItems.map(item => (
            <Link key={item.path} to={item.path} style={S.navItem(location.pathname === item.path)}>
              <span style={{ fontSize: 13, width: 16, textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.path === '/mesajlar' && okunmamis > 0 && (
                <span style={{ background: '#e24b4a', color: 'white', borderRadius: 20, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>
                  {okunmamis > 99 ? '99+' : okunmamis}
                </span>
              )}
              {item.path === '/crm' && yeniAday > 0 && (
                <span style={{ background: 'var(--primary)', color: 'white', borderRadius: 20, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>
                  {yeniAday > 99 ? '99+' : yeniAday}
                </span>
              )}
              {item.path === '/birlestirme-talepleri' && bekleyenBirlestirme > 0 && (
                <span style={{ background: '#e24b4a', color: 'white', borderRadius: 20, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>
                  {bekleyenBirlestirme > 99 ? '99+' : bekleyenBirlestirme}
                </span>
              )}
              {item.path === '/referans-yonetim' && yeniReferans > 0 && (
                <span style={{ background: 'var(--primary)', color: 'white', borderRadius: 20, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>
                  {yeniReferans > 99 ? '99+' : yeniReferans}
                </span>
              )}
            </Link>
          ))}
        </nav>

        <div style={S.sidebarFooter}>
          <div style={S.userRow}>
            {kullanici?.foto_url
              ? <img src={fotoUrl(kullanici.foto_url)} alt="" style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0 }} />
              : <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--active-bg)', color: 'var(--active-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                  {kullanici?.ad?.charAt(0)}
                </div>
            }
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.userName}>{kullanici?.ad}</div>
              <div style={S.userRole}>{kullanici?.rol}</div>
            </div>
            <button onClick={cikisYap} style={S.iconBtn} title="Çıkış">⏻</button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', background: 'var(--bg)', transition: 'background .2s' }}>
        {/* Mobil top bar */}
        <div className="mobile-topbar">
          <button className="hamburger-btn" onClick={() => setSidebarOpen(v => !v)}>
            ☰
          </button>
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>istanbulTANGO</span>
          <div style={{ flex: 1 }} />
          <button onClick={toggle} style={S.iconBtn}>{isDark ? '☀️' : '🌙'}</button>
        </div>

        <div style={{ flex: 1, overflow: 'auto' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
